const STORAGE_KEY_PREFIX = 'yapzap_notification_dismissals_';
const MAX_DISMISSED_IDS = 250;

interface NotificationDismissalState {
  ids: string[];
}

const emptyState: NotificationDismissalState = { ids: [] };

function getStorageKey(userId: string) {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function normalizeIds(ids: unknown): string[] {
  if (!Array.isArray(ids)) {
    return [];
  }

  return Array.from(
    new Set(
      ids
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
        .slice(-MAX_DISMISSED_IDS)
    )
  );
}

function readState(userId: string): NotificationDismissalState {
  if (typeof window === 'undefined') {
    return emptyState;
  }

  try {
    const rawState = window.localStorage.getItem(getStorageKey(userId));
    if (!rawState) {
      return emptyState;
    }

    const parsedState = JSON.parse(rawState) as Partial<NotificationDismissalState>;
    return {
      ids: normalizeIds(parsedState.ids),
    };
  } catch {
    return emptyState;
  }
}

function writeState(userId: string, state: NotificationDismissalState) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    getStorageKey(userId),
    JSON.stringify({
      ids: normalizeIds(state.ids),
    })
  );
}

export function getDismissedNotificationIds(userId: string): string[] {
  return readState(userId).ids;
}

export function dismissNotifications(userId: string, notificationIds: string[]): string[] {
  const currentState = readState(userId);
  const nextIds = normalizeIds([...currentState.ids, ...notificationIds]);

  writeState(userId, { ids: nextIds });

  return nextIds;
}
